import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ImagePicker from "@/components/ImagePicker";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical, Pencil, Eye } from "lucide-react";
import { confirmDialog } from "@/lib/dialogs";

const BK = process.env.REACT_APP_BACKEND_URL;

export default function TestimonialsTab() {
  const [testimonials, setTestimonials] = useState([]);
  const [nt, setNt] = useState({ quote: "", author: "", role: "", industry: "", image_path: "", image_url: "", logo_path: "", logo_url: "" });
  const [editT, setEditT] = useState(null);
  const [pick, setPick] = useState(null); // { kind: "image"|"logo", id: "new"|<tid> }
  const [dragIdx, setDragIdx] = useState(null);

  const reloadT = () => api.get("/testimonials").then((r) => setTestimonials(r.data));
  useEffect(() => { reloadT(); }, []);

  const createTestimonial = async () => {
    if (!nt.quote.trim() && !nt.image_path) { toast.error("Add a quote and/or an image"); return; }
    try {
      await api.post("/testimonials", nt);
      setNt({ quote: "", author: "", role: "", industry: "", image_path: "", image_url: "", logo_path: "", logo_url: "" });
      reloadT(); toast.success("Testimonial added");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
  };
  const delTestimonial = async (id) => {
    if (!(await confirmDialog({ title: "Delete testimonial?", description: "This testimonial will be permanently removed.", tone: "destructive", confirmText: "Delete" }))) return;
    await api.delete(`/testimonials/${id}`); reloadT(); toast.success("Testimonial deleted");
  };
  const toggleTestimonial = async (t) => { await api.put(`/testimonials/${t.id}`, { active: !t.active }); reloadT(); };
  const dropTestimonial = async (i) => {
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); return; }
    const list = [...testimonials];
    const [moved] = list.splice(dragIdx, 1);
    list.splice(i, 0, moved);
    setTestimonials(list); setDragIdx(null);
    try { await api.post("/testimonials/reorder", { ids: list.map((x) => x.id) }); toast.success("Order saved"); }
    catch { toast.error("Could not save order"); reloadT(); }
  };
  const handlePick = async (path, url) => {
    const p = pick; setPick(null);
    if (!p) return;
    const field = p.kind === "logo" ? "logo" : "image";
    if (p.id === "new") { setNt((s) => ({ ...s, [`${field}_path`]: path, [`${field}_url`]: url })); return; }
    try { await api.put(`/testimonials/${p.id}`, { [`${field}_path`]: path }); reloadT(); toast.success(`${field === "logo" ? "Logo" : "Image"} updated`); }
    catch { toast.error("Could not update"); }
  };
  const removeLogo = async (t) => { await api.put(`/testimonials/${t.id}`, { logo_path: "" }); reloadT(); };
  const startEdit = (t) => setEditT({ id: t.id, quote: t.quote || "", author: t.author || "", role: t.role || "", industry: t.industry || "" });
  const saveEdit = async () => {
    try {
      const { id, ...fields } = editT;
      await api.put(`/testimonials/${id}`, fields);
      setEditT(null); reloadT(); toast.success("Testimonial updated");
    } catch { toast.error("Could not save"); }
  };
  const previewSample = async () => {
    try {
      const res = await api.post("/content/preview-pdf", {}, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Could not generate preview"); }
  };

  return (
    <div className="mt-4 space-y-4 max-w-3xl">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-cass-slate">Manage the client stories, logos and order shown on proposals.</p>
        <Button variant="outline" size="sm" onClick={previewSample} data-testid="preview-sample-proposal-btn"><Eye className="w-4 h-4 mr-1" />Preview a test proposal</Button>
      </div>
      <Card className="p-4 rounded-md space-y-3" data-testid="add-testimonial-card">
        <h3 className="font-alt font-semibold text-cass-navy">Add a testimonial</h3>
        <Textarea rows={3} placeholder="Quote / review text (optional if you're adding an image)" value={nt.quote} onChange={(e) => setNt({ ...nt, quote: e.target.value })} data-testid="testimonial-quote" />
        <div className="grid grid-cols-3 gap-2">
          <Input placeholder="Author" value={nt.author} onChange={(e) => setNt({ ...nt, author: e.target.value })} data-testid="testimonial-author" />
          <Input placeholder="Role / company" value={nt.role} onChange={(e) => setNt({ ...nt, role: e.target.value })} data-testid="testimonial-role" />
          <Input placeholder="Industry" value={nt.industry} onChange={(e) => setNt({ ...nt, industry: e.target.value })} data-testid="testimonial-industry" />
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setPick({ kind: "image", id: "new" })} data-testid="testimonial-image-btn">{nt.image_path ? "Change image" : "Add image"}</Button>
          {nt.image_path && <img src={nt.image_url} alt="testimonial" className="h-12 rounded border border-border" />}
          {nt.image_path && <Button variant="ghost" size="sm" onClick={() => setNt({ ...nt, image_path: "", image_url: "" })}>Remove</Button>}
          <span className="w-px h-6 bg-border" />
          <Button variant="outline" size="sm" onClick={() => setPick({ kind: "logo", id: "new" })} data-testid="testimonial-logo-btn">{nt.logo_path ? "Change company logo" : "Add company logo"}</Button>
          {nt.logo_path && <img src={nt.logo_url} alt="logo" className="h-10 object-contain rounded border border-border bg-white p-1" />}
          {nt.logo_path && <Button variant="ghost" size="sm" onClick={() => setNt({ ...nt, logo_path: "", logo_url: "" })}>Remove</Button>}
        </div>
        <Button onClick={createTestimonial} className="bg-cass-navy text-white" data-testid="add-testimonial-btn"><Plus className="w-4 h-4 mr-1" />Add testimonial</Button>
      </Card>
      {testimonials.map((t, i) => (
        <Card key={t.id} draggable onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => dropTestimonial(i)} className={`p-5 rounded-md ${!t.active ? "opacity-60" : ""} ${dragIdx === i ? "ring-2 ring-cass-teal" : ""}`} data-testid={`testimonial-${t.id}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-2 min-w-0">
              <GripVertical className="w-4 h-4 text-cass-slate mt-0.5 cursor-grab shrink-0" data-testid={`drag-testimonial-${t.id}`} />
              <div className="min-w-0 flex-1">
                {t.logo_path && <img src={`${BK}/api/public/files/${t.logo_path}`} alt="company logo" className="mb-2 h-9 object-contain" data-testid={`testimonial-logo-img-${t.id}`} />}
                {editT?.id === t.id ? (
                  <div className="space-y-2" data-testid={`testimonial-edit-${t.id}`}>
                    <Textarea rows={3} value={editT.quote} onChange={(e) => setEditT({ ...editT, quote: e.target.value })} placeholder="Quote / review text" data-testid={`edit-quote-${t.id}`} />
                    <div className="grid grid-cols-3 gap-2">
                      <Input value={editT.author} onChange={(e) => setEditT({ ...editT, author: e.target.value })} placeholder="Author" data-testid={`edit-author-${t.id}`} />
                      <Input value={editT.role} onChange={(e) => setEditT({ ...editT, role: e.target.value })} placeholder="Role / company" data-testid={`edit-role-${t.id}`} />
                      <Input value={editT.industry} onChange={(e) => setEditT({ ...editT, industry: e.target.value })} placeholder="Industry" data-testid={`edit-industry-${t.id}`} />
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" className="bg-cass-navy text-white" onClick={saveEdit} data-testid={`save-testimonial-${t.id}`}>Save</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditT(null)} data-testid={`cancel-testimonial-${t.id}`}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {t.quote && <p className="text-sm text-cass-slate italic">"{t.quote}"</p>}
                    {t.image_path && <img src={`${BK}/api/public/files/${t.image_path}`} alt="testimonial" className="mt-2 max-h-40 rounded border border-border" />}
                    <div className="text-sm font-medium text-cass-navy mt-2">{t.author}{t.role ? ` · ${t.role}` : ""}</div>
                    {t.industry && <Badge variant="secondary" className="mt-1">{t.industry}</Badge>}
                    <div className="mt-2 flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setPick({ kind: "logo", id: t.id })} data-testid={`testimonial-logo-set-${t.id}`}>{t.logo_path ? "Change logo" : "Add company logo"}</Button>
                      {t.logo_path && <Button variant="ghost" size="sm" onClick={() => removeLogo(t)} data-testid={`testimonial-logo-remove-${t.id}`}>Remove logo</Button>}
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <label className="flex items-center gap-1 text-xs text-cass-slate cursor-pointer"><input type="checkbox" checked={!!t.active} onChange={() => toggleTestimonial(t)} data-testid={`testimonial-active-${t.id}`} />Active</label>
              {editT?.id !== t.id && <Button variant="ghost" size="sm" onClick={() => startEdit(t)} data-testid={`edit-testimonial-${t.id}`}><Pencil className="w-4 h-4" /></Button>}
              <Button variant="ghost" size="sm" onClick={() => delTestimonial(t.id)} data-testid={`del-testimonial-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        </Card>
      ))}
      <p className="text-xs text-cass-slate">Drag the <GripVertical className="w-3 h-3 inline" /> handle to reorder how testimonials appear on proposals.</p>
      <ImagePicker open={!!pick} onOpenChange={(o) => { if (!o) setPick(null); }} onSelect={handlePick} />
    </div>
  );
}
